import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { api } from '@/services/api'

export interface SpellcheckOptions {
  /**
   * Debounce time in milliseconds before running spellcheck
   */
  debounce: number
  /**
   * Callback when misspelled words are found
   */
  onSpellcheck?: (misspelled: Array<{ word: string; suggestions: string[] }>) => void
}

interface SpellcheckState {
  decorations: DecorationSet
  misspelledWords: Map<string, string[]> // word -> suggestions
}

const spellcheckPluginKey = new PluginKey<SpellcheckState>('spellcheck')

// Regex to match Amharic words
const AMHARIC_WORD_REGEX = /[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]+/g

export const SpellcheckExtension = Extension.create<SpellcheckOptions>({
  name: 'spellcheck',

  addOptions() {
    return {
      debounce: 1000,
      onSpellcheck: undefined
    }
  },

  addProseMirrorPlugins() {
    const { debounce, onSpellcheck } = this.options
    let timeoutId: NodeJS.Timeout | null = null
    let lastCheckedText = ''

    return [
      new Plugin<SpellcheckState>({
        key: spellcheckPluginKey,

        state: {
          init() {
            return {
              decorations: DecorationSet.empty,
              misspelledWords: new Map()
            }
          },

          apply(tr, oldState) {
            // If document hasn't changed, just map decorations
            if (!tr.docChanged) {
              return {
                ...oldState,
                decorations: oldState.decorations.map(tr.mapping, tr.doc)
              }
            }

            // Document changed - map old decorations for now
            // New decorations will be set via setMeta after spellcheck completes
            const meta = tr.getMeta(spellcheckPluginKey)
            if (meta?.decorations) {
              return {
                decorations: meta.decorations,
                misspelledWords: meta.misspelledWords || oldState.misspelledWords
              }
            }

            return {
              ...oldState,
              decorations: oldState.decorations.map(tr.mapping, tr.doc)
            }
          }
        },

        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? DecorationSet.empty
          }
        },

        view(view) {
          const runSpellcheck = async () => {
            const { doc } = view.state
            const text = doc.textContent

            // Skip if text hasn't changed or is too short
            if (text === lastCheckedText || text.length < 5) {
              return
            }
            lastCheckedText = text

            // Extract Amharic words
            const words: string[] = []
            const wordPositions: Array<{ word: string; from: number; to: number }> = []

            doc.descendants((node: ProseMirrorNode, pos: number) => {
              if (node.isText && node.text) {
                let match
                const regex = new RegExp(AMHARIC_WORD_REGEX.source, 'g')
                while ((match = regex.exec(node.text)) !== null) {
                  const word = match[0]
                  if (word.length >= 2) {
                    words.push(word)
                    wordPositions.push({
                      word,
                      from: pos + match.index,
                      to: pos + match.index + word.length
                    })
                  }
                }
              }
            })

            if (words.length === 0) {
              // Clear decorations if no Amharic words
              const tr = view.state.tr.setMeta(spellcheckPluginKey, {
                decorations: DecorationSet.empty,
                misspelledWords: new Map()
              })
              view.dispatch(tr)
              return
            }

            try {
              // Call spellcheck API
              const uniqueWords = [...new Set(words)]
              const result = await api.spellcheck(uniqueWords)

              // Build map of misspelled words
              const misspelledMap = new Map<string, string[]>()
              for (const item of result.misspelled) {
                misspelledMap.set(item.word, item.suggestions || [])
              }

              // Create decorations for misspelled words
              const decorations: Decoration[] = []
              for (const { word, from, to } of wordPositions) {
                if (misspelledMap.has(word)) {
                  decorations.push(
                    Decoration.inline(from, to, {
                      class: 'spellcheck-error',
                      'data-word': word,
                      'data-suggestions': JSON.stringify(misspelledMap.get(word) || [])
                    })
                  )
                }
              }

              // Update decorations
              const tr = view.state.tr.setMeta(spellcheckPluginKey, {
                decorations: DecorationSet.create(view.state.doc, decorations),
                misspelledWords: misspelledMap
              })
              view.dispatch(tr)

              // Callback with results
              if (onSpellcheck) {
                onSpellcheck(
                  Array.from(misspelledMap.entries()).map(([word, suggestions]) => ({
                    word,
                    suggestions
                  }))
                )
              }
            } catch (error) {
              console.error('Spellcheck error:', error)
            }
          }

          return {
            update(view, prevState) {
              // Only run spellcheck if document changed
              if (view.state.doc.eq(prevState.doc)) {
                return
              }

              // Debounce spellcheck
              if (timeoutId) {
                clearTimeout(timeoutId)
              }
              timeoutId = setTimeout(runSpellcheck, debounce)
            },

            destroy() {
              if (timeoutId) {
                clearTimeout(timeoutId)
              }
            }
          }
        }
      })
    ]
  }
})

// Helper to get misspelled words from editor state
export function getMisspelledWords(state: any): Map<string, string[]> {
  const pluginState = spellcheckPluginKey.getState(state)
  return pluginState?.misspelledWords ?? new Map()
}
